import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { addDays, subDays, format, startOfDay, eachDayOfInterval } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useAccessibleProperties } from "@/hooks/useAccessibleProperties";
import { useMultiPropertyScheduleItems } from "@/hooks/useMultiPropertyScheduleItems";
import type { CleaningTaskWithRelations } from "@/types/cleaning";
import { UnifiedCalendarDrawer } from "@/components/owner/UnifiedCalendarDrawer";
import { UnifiedCalendarGrid } from "@/components/owner/UnifiedCalendarGrid";
import { useTurnoverTaskModal } from "@/contexts/TurnoverTaskModalContext";

const WINDOW_DAYS = 10;
const NAV_SHIFT_DAYS = 7;

interface FullCalendarViewProps {
  /** When set (e.g. team dashboard filter), only show these properties in the calendar */
  restrictToPropertyIds?: string[];
}

/** Full calendar: 10-day window, toolbar, grid, drawer, add-task modal. Use on home Calendar pill or /calendar. */
export function FullCalendarView({ restrictToPropertyIds }: FullCalendarViewProps = {}) {
  const navigate = useNavigate();
  const { openTurnoverTaskModal } = useTurnoverTaskModal();
  const { properties: allProperties, isLoading: propsLoading, showSwitcher } = useAccessibleProperties();

  const properties = useMemo(() => {
    if (restrictToPropertyIds && restrictToPropertyIds.length > 0) {
      const set = new Set(restrictToPropertyIds);
      return allProperties.filter((p) => set.has(p.id));
    }
    return allProperties;
  }, [allProperties, restrictToPropertyIds]);

  const [startDate, setStartDate] = useState(() => startOfDay(new Date()));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<"reservation" | "task">("reservation");
  const [drawerReservation, setDrawerReservation] = useState<Record<string, unknown> | null>(null);
  const [drawerTask, setDrawerTask] = useState<CleaningTaskWithRelations | null>(null);
  const [drawerPropertyId, setDrawerPropertyId] = useState<string | null>(null);
  const [drawerPropertyName, setDrawerPropertyName] = useState<string | null>(null);

  const selectedPropertyIds = useMemo(() => properties.map((p) => p.id), [properties]);
  const visibleDays = useMemo(
    () =>
      eachDayOfInterval({
        start: startDate,
        end: addDays(startDate, WINDOW_DAYS - 1),
      }),
    [startDate]
  );

  const { byPropertyId, isLoading: scheduleLoading } = useMultiPropertyScheduleItems(selectedPropertyIds);

  const goPrev = useCallback(() => setStartDate((d) => subDays(d, NAV_SHIFT_DAYS)), []);
  const goNext = useCallback(() => setStartDate((d) => addDays(d, NAV_SHIFT_DAYS)), []);
  const goToday = useCallback(() => setStartDate(startOfDay(new Date())), []);

  const handleDayClick = useCallback(
    (date: Date, propertyId: string | null) => {
      const pid = propertyId ?? selectedPropertyIds[0] ?? null;
      if (pid) openTurnoverTaskModal({ mode: "create", propertyId: pid, initialDate: date });
    },
    [selectedPropertyIds, openTurnoverTaskModal]
  );

  const openReservationDrawer = useCallback(
    (reservation: Record<string, unknown>, propertyId: string, propertyName: string) => {
      setDrawerReservation(reservation);
      setDrawerTask(null);
      setDrawerPropertyId(propertyId);
      setDrawerPropertyName(propertyName);
      setDrawerMode("reservation");
      setDrawerOpen(true);
    },
    []
  );
  const openTaskDrawer = useCallback(
    (task: CleaningTaskWithRelations, propertyId: string, propertyName: string) => {
      setDrawerTask(task);
      setDrawerReservation(null);
      setDrawerPropertyId(propertyId);
      setDrawerPropertyName(propertyName);
      setDrawerMode("task");
      setDrawerOpen(true);
    },
    []
  );
  const openTurnoverNeeded = useCallback(
    (reservation: Record<string, unknown>, propertyId: string, propertyName: string) => {
      setDrawerReservation(reservation);
      setDrawerTask(null);
      setDrawerPropertyId(propertyId);
      setDrawerPropertyName(propertyName);
      setDrawerMode("reservation");
      setDrawerOpen(true);
    },
    []
  );

  const title =
    visibleDays.length > 0
      ? `${format(visibleDays[0], "MMM d")} – ${format(visibleDays[visibleDays.length - 1], "MMM d, yyyy")}`
      : "";
  if (!showSwitcher || properties.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No properties available. Add a property to use the calendar.
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={goPrev}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={goNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <span className="min-w-[200px] font-semibold text-foreground">{title}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToday}>
              Today
            </Button>
            <Button
              size="sm"
              onClick={() => {
                const pid = selectedPropertyIds[0];
                if (pid) openTurnoverTaskModal({ mode: "create", propertyId: pid, initialDate: visibleDays[0] ?? new Date() });
              }}
              disabled={selectedPropertyIds.length === 0}
            >
              Add task
            </Button>
          </div>
        </div>

        <motion.div
          layout
          className="rounded-xl border border-border bg-card shadow-sm overflow-x-auto overflow-y-hidden min-w-0"
          style={{ maxWidth: "100%" }}
        >
          <UnifiedCalendarGrid
            properties={properties}
            visibleDays={visibleDays}
            windowStart={startDate}
            byPropertyId={byPropertyId}
            onReservationClick={openReservationDrawer}
            onTaskClick={openTaskDrawer}
            onTurnoverNeededClick={openTurnoverNeeded}
            onDayClick={handleDayClick}
            onAddTaskOnDate={handleDayClick}
            isLoading={scheduleLoading}
            isPropsLoading={propsLoading}
          />
        </motion.div>
      </div>

      <UnifiedCalendarDrawer
        open={drawerOpen}
        onOpenChange={(open) => {
          setDrawerOpen(open);
          if (!open) {
            setDrawerTask(null);
            setDrawerReservation(null);
            setDrawerPropertyId(null);
            setDrawerPropertyName(null);
          }
        }}
        mode={drawerMode}
        reservation={drawerReservation}
        task={drawerTask}
        propertyId={drawerPropertyId}
        propertyName={drawerPropertyName}
        onOpenInPropertySchedule={(id, focus) => {
          setDrawerOpen(false);
          const params = new URLSearchParams({ tab: "schedule", ...focus });
          navigate(`/properties/${id}?${params.toString()}`);
        }}
        onTaskCreated={() => {
          setDrawerMode("reservation");
          setDrawerReservation(drawerReservation);
          setDrawerTask(null);
        }}
        onSwitchToTask={(task) => {
          setDrawerTask(task as CleaningTaskWithRelations);
          setDrawerReservation(null);
          setDrawerMode("task");
        }}
      />
    </>
  );
}
